/**
 * Recuperação de aba aberta durante um deploy.
 *
 * As páginas são carregadas sob demanda (`lazy` no App), e cada build gera
 * nomes de arquivo com hash novo. Quem estava com a aba aberta quando sai um
 * deploy tem, no HTML já carregado, o caminho de um chunk que não existe mais:
 * na primeira navegação o import falha e a tela mostra "Esta página falhou".
 * Foi o que aconteceu no Instituto Chinellato em 01/09/2026, e um F5 resolvia.
 *
 * A saída é recarregar sozinho, uma vez. O risco de recarregar em resposta a
 * erro é o laço: se o problema não for o chunk, a página recarrega, falha de
 * novo, recarrega de novo, e o usuário fica preso sem nem conseguir ler o
 * erro. Por isso a guarda por tempo abaixo.
 */

/** Janela em que uma segunda recarga é considerada laço, não recuperação. */
const JANELA_DE_GUARDA_MS = 30_000;

const CHAVE = 'recarga-por-chunk-velho';

/**
 * Mensagens dos navegadores para "o módulo que você pediu não está mais lá".
 * Cada um escreve a sua, e nenhuma tem código de erro para checar.
 */
const SINAIS = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to preload css', // Vite, ao pré-carregar o CSS do chunk
  'failed to load module script', // Chrome, quando o servidor devolve HTML no lugar do JS
];

export function ehErroDeChunkVelho(erro: unknown): boolean {
  const texto = erro instanceof Error
    ? `${erro.name} ${erro.message}`
    : typeof erro === 'string'
      ? erro
      : '';
  const normalizado = texto.toLowerCase();
  return SINAIS.some((sinal) => normalizado.includes(sinal));
}

/**
 * Recarrega a página, no máximo uma vez por janela de guarda.
 *
 * Devolve `false` quando decide NÃO recarregar, e nesse caso quem chamou deve
 * seguir mostrando o erro: é o sinal de que recarregar não resolveu e insistir
 * só prenderia o usuário num laço.
 */
export function recarregarUmaVezPorChunkVelho(agora: number = Date.now()): boolean {
  let ultima = 0;
  try {
    ultima = Number(sessionStorage.getItem(CHAVE)) || 0;
  } catch {
    // Navegador com storage bloqueado: sem memória entre recargas, não dá para
    // garantir que isso não vira laço. Melhor não recarregar.
    return false;
  }

  if (agora - ultima < JANELA_DE_GUARDA_MS) return false;

  try {
    sessionStorage.setItem(CHAVE, String(agora));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}

export const _paraTeste = { JANELA_DE_GUARDA_MS, CHAVE, SINAIS };
